import dayjs from 'dayjs';
import { parse } from 'querystring';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from 'src/AppContext';
import Badge from 'src/components/Badge';
import Button from 'src/components/Button';
import LoadingBlock from 'src/components/LoadingBlock';
import PromotionChart from 'src/components/PromotionChart';
import { PRODUCT_REGION, USER_PRODUCT_STATUS } from 'src/constants/product';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { useModal } from 'src/ModalContext';
import { userProductByOrderId } from 'src/services/api';
import { getProduct } from 'src/services/checkout';
import { getCurrency, getParseQueryString, isKorean } from 'src/services/util';

const onPolling = (callback: () => Promise<boolean>) => {
  return new Promise((resolve, reject) => {
    let timeIndex = 0;
    let timer;

    const _onStart = async () => {
      try {
        await callback();
        console.log('polling timer ' + timeIndex + ' ---- true');
        timer && clearTimeout(timer);
        resolve(true);
      } catch (e) {
        console.log('polling timer ' + timeIndex + ' ---- ');
        timer = setTimeout(() => {
          if (timeIndex + 1 > 10) {
            console.log('polling timer ' + timeIndex + ' ---- length');
            timer && clearTimeout(timer);
            reject(e);
            return;
          }
          timeIndex++;
          _onStart();
        }, 500);
      }
    };
    _onStart();
  });
};

export default function PaymentComplete(): JSX.Element {
  const { t } = useTranslation();
  const history = useCustomHistory();
  const location = useLocation();
  const { orderId, productId, imp_success, error_msg, payMethod, afterPaymentEndpoint } = getParseQueryString(location.search);
  const [myProduct, setMyProduct] = useState<CheckoutMyProductData>({} as CheckoutMyProductData);
  const [product, setProduct] = useState<CheckoutProductData>({} as CheckoutProductData);
  const dispatch = useAppDispatch();
  const { errorModal } = useModal();
  const [isLoadingShow, setIsLoadingShow] = useState(true);

  useEffect(() => {
    // alert('imp_success : ' + imp_success);
    // alert('!imp_success : ' + !imp_success);
    // alert('JSON.parse(imp_success as string) === false : ' + (JSON.parse(imp_success as string) === false).toString());
    // alert('productId : ' + productId);
    if (!imp_success || JSON.parse(imp_success as string) === false) {
      history.replace('/payment/fail', {
        orderId: orderId as string,
        productId: productId as string,
        payMethod: payMethod as string,
        imp_success: imp_success as string,
        error_msg: error_msg as string,
      });
      return;
    }
    if (productId) {
      getProduct(productId as string)
        .then((res) => {
          setProduct(res);
        })
        .catch(errorModal);
    }

    if (orderId) {
      onPolling(() => {
        setIsLoadingShow(true);
        return new Promise((resolve, reject) => {
          userProductByOrderId({
            orderId: orderId as string,
          })
            .then((res) => {
              if (res && res.myProduct) {
                setIsLoadingShow(false);
                resolve(true);
                setMyProduct(res.myProduct);
              }
            })
            .catch((e) => {
              reject(e);
              // history.replace('/payment/fail', {
              //   orderId: orderId as string,
              //   productId: productId as string,
              //   imp_success: 'false',
              //   error_msg: e.message,
              // });
              // showModal({
              //   message: e.message,
              //   success: () => {
              //     console.log('success');
              //   },
              // });
            });
        });
      }).catch((e) => {
        history.replace('/payment/fail', {
          orderId: orderId as string,
          productId: productId as string,
          imp_success: 'false',
          error_msg: e.message,
        });
      });
    }
  }, [imp_success]);

  useLayoutEffect(() => {
    dispatch({ type: 'SET_GNB_LEFT', name: 'hidden' });
    dispatch({ type: 'SET_GNB_RIGHT', name: 'close' });
    dispatch({
      type: 'SET_GNB_RIGHT_CALLBACK',
      cb: () => {
        if (afterPaymentEndpoint) {
          const urls = (afterPaymentEndpoint as string).split('?');
          history.push(urls[0] ? urls[0] : '/', urls[1] && urls[1].length > 0 ? (parse(urls[1]) as Record<string, string>) : null, [
            'afterPaymentEndpoint',
          ]);
        } else {
          history.push('/');
        }
      },
    });
    return () => {
      dispatch({ type: 'RESET_GNB_LEFT' });
      dispatch({ type: 'RESET_GNB_RIGHT' });
      dispatch({ type: 'RESET_GNB_RIGHT_CALLBACK' });
    };
  }, []);

  const onClickMyProduct = useCallback(() => {
    history.push('/product/my', {
      tab: product.category,
    });
  }, [product]);

  return (
    <>
      <div className={isLoadingShow ? 'hidden' : 'block'}>
        <div className="container mt-6">
          {product.region === PRODUCT_REGION.GLOBAL && product.subscription === true ? (
            <div>
              <h1 className="N_20_R text-black">{t('payment/complete/complete_auto_title')}</h1>
              <div className="N_12_R mt-3 text-brown-grey">{t('payment/complete/complete_auto_message')}</div>
            </div>
          ) : (
            <h1 className="N_20_R text-black">{t('payment/complete/complete_title')}</h1>
          )}
        </div>
        <div className="mt-10 border-t border-ice-blue bg-light-gray-1 pt-8">
          <div className="container">
            {myProduct.status !== USER_PRODUCT_STATUS.SUB_CREATED && myProduct.displayPaymentId !== '' && (
              <div className="flex N_12_R text-brown-grey items-center mb-5">
                <span>{t('payment/complete/payment_id')}</span>
                <span className="division mx-1.5 bg-ice-blue"></span>
                <span>{myProduct.displayPaymentId}</span>
              </div>
            )}
            {product.subscription && (
              <div className="flex mb-3">
                <Badge color="light-grey-blue" title={t('payment/complete/badge_billing')}></Badge>
              </div>
            )}
            {/* <div className="flex justify-between">
            </div> */}
            <div className="N_20_R text-black">{isKorean() ? product.koName : product.enName}</div>
            {product.subscription && myProduct.firstPayment && product.promotionAmount > 0 ? (
              <ul className="mt-12">
                <li className="flex justify-between items-start">
                  <div className="N_14_R text-brown-grey">{t('payment/billing_period_title')}</div>
                  <div className="N_14_R text-greyish-brown text-right" style={{ maxWidth: '165px' }}>
                    {product.durationType && (
                      <Trans
                        i18nKey={`payment/billing_period_${product.durationType.toLowerCase()}`}
                        values={{ period: product.duration }}
                      ></Trans>
                    )}
                  </div>
                </li>
                <li className="flex justify-between mt-2">
                  <div className="N_14_R text-brown-grey">{t('payment/service_period_title')}</div>
                  <div className="N_14_R text-greyish-brown">
                    {product.durationType && (
                      <Trans
                        i18nKey={`payment/service_period_${product.durationType.toLowerCase()}`}
                        values={{ period: product.duration }}
                      ></Trans>
                    )}
                  </div>
                </li>
                <li className="flex justify-between mt-2">
                  <div className="N_14_R text-brown-grey">
                    <Trans i18nKey="payment/cover_price_subscription_title" values={{ promotionCycle: product.promotionCycle + 1 }}></Trans>
                  </div>
                  <div className="N_14_R text-greyish-brown">
                    <NumberFormat
                      value={product.amount}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={getCurrency(product.region)}
                    />
                  </div>
                </li>
                <li className="flex justify-between mt-2">
                  <div className="N_14_R text-brown-grey">
                    <Trans i18nKey="payment/promotion_subscription_title" values={{ promotionCycle: product.promotionCycle }}></Trans>
                  </div>
                  <div className="N_14_R text-greyish-brown">
                    -{' '}
                    <NumberFormat
                      value={product.amount - product.promotionAmount}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={getCurrency(product.region)}
                    />
                  </div>
                </li>
              </ul>
            ) : (
              <ul className="mt-12">
                <li className="flex justify-between">
                  <div className="N_14_R text-brown-grey">{t('payment/service_period_title')}</div>
                  <div className="N_14_R inline-flex flex-col items-end">
                    <div className="text-greyish-brown">
                      {product.durationType && (
                        <Trans
                          i18nKey={`payment/service_period_${product.durationType.toLowerCase()}`}
                          values={{ period: product.duration }}
                        ></Trans>
                      )}
                    </div>
                    <div className="text-brown-grey">
                      {myProduct.endDate && (
                        <Trans
                          i18nKey="payment/complete/service_period_date"
                          values={{ date: dayjs(myProduct.endDate).format('YYYY.MM.DD') }}
                        ></Trans>
                      )}
                    </div>
                  </div>
                </li>
                <li className="flex justify-between mt-2">
                  <div className="N_14_R text-brown-grey">{t('payment/cover_price_title')}</div>
                  <div className="N_14_R text-greyish-brown">
                    <NumberFormat
                      value={product.amount}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={getCurrency(product.region)}
                    />
                  </div>
                </li>
                {myProduct.promotionId && (
                  <li className="flex justify-between mt-2">
                    <div className="N_14_R text-brown-grey">{t('payment/promotion_title')}</div>
                    <div className="N_14_R text-greyish-brown">
                      -{' '}
                      <NumberFormat
                        value={myProduct.saleAmount}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={getCurrency(product.region)}
                      />
                    </div>
                  </li>
                )}
              </ul>
            )}
            <div className="mt-6 border-t border-ice-blue pt-7 pb-9 flex justify-between items-center">
              <div className="N_14_R">
                <span>{t('payment/purchase_amount')}</span>
                <span className="text-brown-grey ml-1">({t('payment/purchase_vat')})</span>
              </div>
              <div className="N_22_R text-black">
                <NumberFormat
                  value={
                    product.subscription && myProduct.firstPayment && product.promotionAmount > 0
                      ? product.promotionAmount
                      : myProduct.promotionId
                      ? product.amount - myProduct.saleAmount
                      : product.amount
                  }
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={getCurrency(product.region)}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          {product.subscription && myProduct.firstPayment && product.promotionAmount > 0 && <PromotionChart product={product} />}
          <div className="mt-11 mb-10">
            <Button theme="sub" title={t('payment/complete/my_product_button')} onClick={onClickMyProduct}></Button>
          </div>
        </div>
      </div>
      <LoadingBlock title={t('payment/payment_loading')} isShow={isLoadingShow} />
    </>
  );
}
